import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Image, Search, X, RefreshCw } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE = 100;

function authHeaders() {
  const h = {};
  if (window.__sessionId) h['X-Session-ID'] = window.__sessionId;
  return h;
}

export default function InventoryPictures() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [imageFilter, setImageFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);
  const activeQuery = useRef(''); // tracks what was last fetched

  const doFetch = useCallback(async ({ q = '', pageNum = 1, append = false }) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, page: pageNum, sortBy: 'code', order: 'ASC' });
      if (q) params.set('code_prefix', q);
      const res = await fetch(`${API_BASE}/inventory?${params}`, {
        credentials: 'include',
        headers: authHeaders()
      });
      const data = await res.json();
      const rows = data.items || data.data || [];
      setItems(prev => append ? [...prev, ...rows] : rows);
      setTotal(data.total || 0);
      setPage(pageNum);
      activeQuery.current = q;
    } catch (e) {
      console.error('InventoryPictures fetch error', e);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { doFetch({ q: '', pageNum: 1 }); }, [doFetch]);

  // Debounced server search when search text changes
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doFetch({ q: search.trim(), pageNum: 1 });
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, doFetch]);

  // Image filter only — server already handles the search query
  const filtered = items.filter(item =>
    imageFilter === 'all' ? true :
    imageFilter === 'with' ? !!item.image_url :
    !item.image_url
  );

  const withImg = items.filter(i => i.image_url).length;
  const withoutImg = items.length - withImg;
  const hasMore = items.length < total;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-slate-50">
      <PageIdentifier pageId={SCREEN_IDS.INVENTORY.PICTURES} pageName="Inventory Pictures" />

      {/* ── Top bar ── */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-wrap shadow-sm">
        <h1 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 flex-shrink-0">
          <Image size={15} className="text-blue-500" />
          Inventory Pictures
        </h1>

        {/* Search */}
        <div className="relative flex-1 min-w-40 max-w-64">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search code, name…"
            className="w-full pl-7 pr-7 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={12} />
            </button>
          )}
        </div>

        {/* Image filter */}
        <div className="flex items-center gap-1">
          {[['all', 'All Photos'], ['with', `With (${withImg})`], ['without', `Missing (${withoutImg})`]].map(([v, l]) => (
            <button key={v} onClick={() => setImageFilter(v)}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition ${imageFilter === v ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Stats + refresh */}
        <div className="ml-auto flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-slate-400">
            {filtered.length} shown · {total} total
          </span>
          <button onClick={() => doFetch({ q: search.trim(), pageNum: 1 })} disabled={loading}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-40 transition">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Gallery grid ── */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-400">
            <RefreshCw size={20} className="animate-spin mr-2" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-400 gap-2">
            <Image size={32} className="text-slate-200" />
            <p className="text-sm">No items match your filters</p>
          </div>
        ) : (
          <>
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
              {filtered.map(item => (
                <button
                  key={item.id}
                  onClick={() => setSelected(item)}
                  className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md hover:border-blue-300 transition-all text-left"
                >
                  {/* Image */}
                  <div className="w-full h-28 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.code}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-slate-300">
                        <Image size={24} />
                        <span className="text-xs">No image</span>
                      </div>
                    )}
                  </div>

                  {/* Info strip */}
                  <div className="px-1.5 py-1 border-t border-slate-100">
                    <p className="text-xs font-bold text-slate-700 truncate">{item.code}</p>
                    {item.category && (
                      <p className="text-xs text-slate-400 truncate">{item.category}</p>
                    )}
                  </div>

                  {/* Status dot */}
                  <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${item.status === 'In Stock' ? 'bg-green-500' : item.status === 'Sold' ? 'bg-red-400' : 'bg-slate-400'}`} title={item.status} />
                </button>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="flex justify-center mt-4 pb-2">
                <button
                  onClick={() => doFetch({ q: search.trim(), pageNum: page + 1, append: true })}
                  disabled={loadingMore}
                  className="px-5 py-2 bg-slate-700 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-50 transition flex items-center gap-2">
                  {loadingMore
                    ? <><RefreshCw size={12} className="animate-spin" /> Loading…</>
                    : `Load more (${total - items.length} remaining)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Lightbox ── */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-lg w-full"
            onClick={e => e.stopPropagation()}
          >
            {/* Image */}
            <div className="bg-slate-900 flex items-center justify-center" style={{ minHeight: 260 }}>
              {selected.image_url ? (
                <img
                  src={selected.image_url}
                  alt={selected.code}
                  className="max-h-72 max-w-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-600 py-16">
                  <Image size={40} />
                  <span className="text-sm">No image available</span>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <h2 className="text-base font-bold text-slate-800">{selected.code}</h2>
                  {selected.name && selected.name !== selected.code && (
                    <p className="text-sm text-slate-500">{selected.name}</p>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${selected.status === 'In Stock' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {selected.status}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs mb-4">
                {selected.category && (
                  <div><p className="text-slate-400 uppercase tracking-wide">Category</p><p className="font-semibold text-slate-700">{selected.category}</p></div>
                )}
                {selected.gold_purity && (
                  <div><p className="text-slate-400 uppercase tracking-wide">Purity</p><p className="font-semibold text-slate-700">{selected.gold_purity}K</p></div>
                )}
                {selected.gross_weight && (
                  <div><p className="text-slate-400 uppercase tracking-wide">Gross Wt</p><p className="font-semibold text-slate-700">{parseFloat(selected.gross_weight).toFixed(2)}g</p></div>
                )}
                {selected.net_weight && (
                  <div><p className="text-slate-400 uppercase tracking-wide">Net Wt</p><p className="font-semibold text-slate-700">{parseFloat(selected.net_weight).toFixed(2)}g</p></div>
                )}
                {selected.sale_price > 0 && (
                  <div><p className="text-slate-400 uppercase tracking-wide">Price</p><p className="font-semibold text-slate-700">₹{parseInt(selected.sale_price).toLocaleString('en-IN')}</p></div>
                )}
              </div>

              <button onClick={() => setSelected(null)}
                className="w-full py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold text-slate-700 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
